import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import api from '../../api';
import './CapacityManagementModal.css';

export default function CapacityManagementModal({ sprintId, sprintName, sprintStartDate, sprintEndDate, onClose }) {
  const [workload, setWorkload] = useState(null);
  const [teamCapacity, setTeamCapacity] = useState(null);
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingCapacity, setEditingCapacity] = useState(null);
  const modalBodyRef = useRef(null);
  const modalContentRef = useRef(null);
  const [capacityForm, setCapacityForm] = useState({
    person_id: '',
    week_start_date: '',
    capacity_hours: '',
    capacity_points: '',
    notes: ''
  });

  // Scroll to top immediately when modal opens (before paint)
  useLayoutEffect(() => {
    if (modalBodyRef.current) {
      modalBodyRef.current.scrollTop = 0;
    }
    if (modalContentRef.current) {
      modalContentRef.current.scrollTop = 0;
    }
  }, []);

  // Also scroll to top after content loads
  useEffect(() => {
    if (modalBodyRef.current) {
      const timer = setTimeout(() => {
        if (modalBodyRef.current) {
          modalBodyRef.current.scrollTop = 0;
        }
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [workload, teamCapacity]);

  useEffect(() => {
    if (sprintId) {
      loadWorkload();
      loadTeamCapacity();
      loadPeople();
    }
  }, [sprintId]);

  const loadWorkload = async () => {
    try {
      const res = await api.get(`/capacity/sprint/${sprintId}/assignees`);
      setWorkload(res.data);
    } catch (err) {
      console.error('Failed to load workload:', err);
      alert('Failed to load assignee workload.');
    }
  };

  const loadTeamCapacity = async () => {
    try {
      const res = await api.get(`/capacity/sprint/${sprintId}/team-capacity`);
      setTeamCapacity(res.data);
    } catch (err) {
      console.error('Failed to load team capacity:', err);
      alert('Failed to load team capacity.');
    }
  };

  const loadPeople = async () => {
    try {
      const res = await api.get('/tasks/people');
      setPeople(res.data || []);
    } catch (err) {
      console.error('Failed to load people:', err);
    }
  };

  const getWeeksInSprint = () => {
    if (!sprintStartDate || !sprintEndDate) return [];
    const weeks = new Set();
    
    // Parse dates properly - handle YYYY-MM-DD format
    const start = new Date(sprintStartDate);
    const end = new Date(sprintEndDate);
    
    // Validate dates
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      console.error('Invalid date range:', sprintStartDate, sprintEndDate);
      return [];
    }
    
    // Get the Monday of the week containing the start date
    const startMonday = getMonday(start);
    if (startMonday) weeks.add(startMonday);
    
    // Get the Monday of the week containing the end date
    const endMonday = getMonday(end);
    if (endMonday) weeks.add(endMonday);
    
    // Add all Mondays in between
    if (startMonday && endMonday) {
      let currentMonday = new Date(startMonday);
      currentMonday.setDate(currentMonday.getDate() + 7); // Move to next Monday
      const endMondayDate = new Date(endMonday);
      
      while (currentMonday <= endMondayDate) {
        const mondayStr = formatDateString(currentMonday);
        if (mondayStr) weeks.add(mondayStr);
        currentMonday.setDate(currentMonday.getDate() + 7);
      }
    }
    
    return Array.from(weeks).sort();
  };

  const getMonday = (date) => {
    if (!date || isNaN(date.getTime())) return null;
    
    const d = new Date(date);
    const day = d.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust to get Monday
    const monday = new Date(d.getFullYear(), d.getMonth(), diff);
    return formatDateString(monday);
  };

  const formatDateString = (date) => {
    if (!date || isNaN(date.getTime())) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleSetCapacity = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await api.post('/capacity', capacityForm);
      // Reload both workload and team capacity to reflect the changes
      await Promise.all([
        loadWorkload(),
        loadTeamCapacity()
      ]);
      setEditingCapacity(null);
      setCapacityForm({
        person_id: '',
        week_start_date: '',
        capacity_hours: '',
        capacity_points: '',
        notes: ''
      });
      alert('Capacity set successfully!');
    } catch (err) {
      console.error('Failed to set capacity:', err);
      alert('Failed to set capacity. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const planningMethod = workload?.sprint?.planning_method || teamCapacity?.sprint?.planning_method || 'hours';
  const isHours = planningMethod === 'hours';

  return (
    <div className="capacity-modal-overlay" onClick={onClose}>
      <div className="capacity-modal-content" ref={modalContentRef} onClick={(e) => e.stopPropagation()}>
        <div className="capacity-modal-header">
          <h2>Capacity & Workload Management</h2>
          <button className="capacity-modal-close" onClick={onClose}>×</button>
        </div>

        <div className="capacity-modal-body" ref={modalBodyRef}>
          {workload && (
            <div className="capacity-section">
              <h3>Assignee Workload - {sprintName}</h3>
              <div className="workload-table">
                <table>
                  <thead>
                    <tr>
                      <th>Assignee</th>
                      <th>Tasks</th>
                      <th>{isHours ? 'Hours' : 'Points'} Assigned</th>
                      <th>{isHours ? 'Hours' : 'Points'} Capacity</th>
                      <th>{isHours ? 'Hours' : 'Points'} Remaining</th>
                      <th>Utilization</th>
                    </tr>
                  </thead>
                  <tbody>
                    {workload.assignees && workload.assignees.length > 0 ? (
                      workload.assignees.map((assignee) => {
                        const assigned = isHours ? assignee.assigned.hours : assignee.assigned.points;
                        const capacity = isHours ? assignee.capacity.hours : assignee.capacity.points;
                        const remaining = isHours ? assignee.remaining.hours : assignee.remaining.points;
                        const utilization = assignee.utilization_percent || '0';
                        const isOverCapacity = remaining < 0;
                        
                        return (
                          <tr key={assignee.person_id} className={isOverCapacity ? 'over-capacity' : ''}>
                            <td>{assignee.person_name}</td>
                            <td>{assignee.task_count}</td>
                            <td>{assigned.toFixed(1)}</td>
                            <td>{capacity.toFixed(1)}</td>
                            <td className={isOverCapacity ? 'negative' : ''}>
                              {remaining.toFixed(1)}
                            </td>
                            <td>
                              <span className={`utilization ${parseFloat(utilization) > 100 ? 'over' : parseFloat(utilization) > 80 ? 'high' : ''}`}>
                                {utilization}%
                              </span>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="6" style={{ textAlign: 'center', color: 'rgba(255, 255, 255, 0.6)' }}>
                          No assignees in this sprint
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {teamCapacity && (
            <div className="capacity-section">
              <h3>Team Capacity Summary</h3>
              {teamCapacity.totals && (
                <div className="capacity-totals">
                  <div className="total-item">
                    <span className="total-label">Total {isHours ? 'Hours' : 'Points'} Capacity:</span>
                    <span className="total-value">{isHours ? teamCapacity.totals.capacity.hours.toFixed(1) : teamCapacity.totals.capacity.points}</span>
                  </div>
                  <div className="total-item">
                    <span className="total-label">Total {isHours ? 'Hours' : 'Points'} Assigned:</span>
                    <span className="total-value">{isHours ? teamCapacity.totals.assigned.hours.toFixed(1) : teamCapacity.totals.assigned.points}</span>
                  </div>
                  <div className="total-item">
                    <span className="total-label">Total {isHours ? 'Hours' : 'Points'} Remaining:</span>
                    <span className={`total-value ${(isHours ? teamCapacity.totals.remaining.hours : teamCapacity.totals.remaining.points) < 0 ? 'negative' : ''}`}>
                      {isHours ? teamCapacity.totals.remaining.hours.toFixed(1) : teamCapacity.totals.remaining.points}
                    </span>
                  </div>
                </div>
              )}
              
              <div className="team-capacity-table">
                <table>
                  <thead>
                    <tr>
                      <th>Team Member</th>
                      <th>{isHours ? 'Hours' : 'Points'} Capacity</th>
                      <th>{isHours ? 'Hours' : 'Points'} Assigned</th>
                      <th>{isHours ? 'Hours' : 'Points'} Remaining</th>
                    </tr>
                  </thead>
                  <tbody>
                    {teamCapacity.team && teamCapacity.team.length > 0 ? (
                      teamCapacity.team.map((member) => {
                        const capacity = isHours ? member.capacity.hours : member.capacity.points;
                        const assigned = isHours ? member.assigned.hours : member.assigned.points;
                        const remaining = isHours ? member.remaining.hours : member.remaining.points;
                        const isOverCapacity = remaining < 0;
                        
                        return (
                          <tr key={member.person_id} className={isOverCapacity ? 'over-capacity' : ''}>
                            <td>{member.person_name}</td>
                            <td>{capacity.toFixed(1)}</td>
                            <td>{assigned.toFixed(1)}</td>
                            <td className={isOverCapacity ? 'negative' : ''}>
                              {remaining.toFixed(1)}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="4" style={{ textAlign: 'center', color: 'rgba(255, 255, 255, 0.6)' }}>
                          No team members with capacity or assignments
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="capacity-section">
            <h3>Set Capacity</h3>
            <form onSubmit={handleSetCapacity} className="capacity-form">
              <div className="form-row">
                <div className="form-field">
                  <label>Person</label>
                  <select
                    value={capacityForm.person_id}
                    onChange={(e) => setCapacityForm({ ...capacityForm, person_id: e.target.value })}
                    required
                  >
                    <option value="">Select person</option>
                    {people.map((person) => (
                      <option key={person.id} value={person.id}>
                        {person.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-field">
                  <label>Week Start (Monday)</label>
                  <select
                    value={capacityForm.week_start_date}
                    onChange={(e) => setCapacityForm({ ...capacityForm, week_start_date: e.target.value })}
                    required
                  >
                    <option value="">Select week</option>
                    {getWeeksInSprint().map((week) => (
                      <option key={week} value={week}>
                        {new Date(week).toLocaleDateString()}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-field">
                  <label>Capacity Hours</label>
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    value={capacityForm.capacity_hours}
                    onChange={(e) => setCapacityForm({ ...capacityForm, capacity_hours: e.target.value })}
                  />
                </div>
                <div className="form-field">
                  <label>Capacity Points</label>
                  <input
                    type="number"
                    min="0"
                    value={capacityForm.capacity_points}
                    onChange={(e) => setCapacityForm({ ...capacityForm, capacity_points: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-field">
                <label>Notes (optional)</label>
                <textarea
                  value={capacityForm.notes}
                  onChange={(e) => setCapacityForm({ ...capacityForm, notes: e.target.value })}
                  rows="2"
                />
              </div>
              <button type="submit" disabled={loading} className="capacity-submit-btn">
                {loading ? 'Setting...' : 'Set Capacity'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

